import React, { useEffect, useRef, useState } from 'react';

/**
 * Reveal — IntersectionObserver-driven fade-up wrapper.
 *
 * Single-shot: disconnects after the first reveal so the animation
 * never replays on re-scroll. delay is in ms — used to stagger sibling
 * cards (`{cards.map((c, i) => <Reveal key={c} delay={i * 80}>...))`).
 *
 * Falls back to fully-visible when IntersectionObserver isn't
 * available (older browsers, SSR) so content is never hidden behind a
 * missing observer. Respects prefers-reduced-motion: when set, we skip
 * the transition entirely and render content visible immediately.
 */
export default function Reveal({ children, delay = 0, className = '', y = 16 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  const reduced = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (reduced) { setShown(true); return; }
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') { setShown(true); return; }
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setShown(true); obs.disconnect(); } },
      { threshold: 0.1, rootMargin: '0px 0px -8% 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [reduced]);

  const visible = shown || reduced;
  return (
    <div ref={ref} className={className} style={{
      transition: reduced ? undefined : 'opacity 0.6s ease-out, transform 0.6s ease-out',
      transitionDelay: reduced ? undefined : `${delay}ms`,
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : `translateY(${y}px)`,
      willChange: visible ? 'auto' : 'opacity, transform',
    }}>
      {children}
    </div>
  );
}
